const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// The cutoff rule below is about when the *courier* can start processing an
// order, so it has to run on the store's local clock (Gulf Standard Time,
// UTC+4, no DST) rather than the visitor's browser timezone - a shopper
// browsing from another timezone would otherwise get a wrong answer.
const SHOP_UTC_OFFSET_MINUTES = 4 * 60;

const getShopNow = () => {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + SHOP_UTC_OFFSET_MINUTES * 60000);
};

// No deliveries on Saturday or Sunday. On top of that, the courier can't
// start processing an order placed from 12:00 PM Saturday through the end
// of Sunday (i.e. until 12 AM Monday) until Monday, so that window adds
// extra days: +2 for a Saturday-afternoon order, +1 for a Sunday order.
const getOrderTimeExtraDays = (shopNow) => {
  const dayOfWeek = shopNow.getDay();
  if (dayOfWeek === 6 && shopNow.getHours() >= 12) return 2;
  if (dayOfWeek === 0) return 1;
  return 0;
};

// `days` (plus any order-time extra days above) is counted in delivery
// days here, skipping Saturday (getDay() === 6) and Sunday (getDay() === 0).
const getExpectedDeliveryDate = (days) => {
  const shopNow = getShopNow();
  const date = new Date(shopNow.getTime());
  let remaining = days + getOrderTimeExtraDays(shopNow);

  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      remaining -= 1;
    }
  }

  return `${date.getDate()} ${MONTH_ABBREVIATIONS[date.getMonth()]}`;
};

const lookupEntry = (data, key) => {
  if (!data) return null;

  return data.entries?.[key] ?? data.fallback ?? null;
};

class PincodeChecker extends HTMLElement {
  connectedCallback() {
    this.defaultCity = this.querySelector('[data-field="default-city"]');
    this.defaultDate = this.querySelector('[data-field="default-date"]');
    this.overrideView = this.querySelector('[data-pincode-override]');
    this.toggleButton = this.querySelector('[data-pincode-toggle]');
    this.listbox = this.querySelector('[data-pincode-listbox]');
    this.options = [...this.querySelectorAll('.pincode-checker__option')];
    this.result = this.querySelector('[data-pincode-result]');
    this.successTemplate = this.querySelector('[data-pincode-template="success"]');
    this.unavailableTemplate = this.querySelector('[data-pincode-template="unavailable"]');
    this.dataUrl = this.dataset.src;
    // Keying the cache by the full URL (not just the country) means a redeploy
    // automatically invalidates it: Shopify appends a version query string to
    // asset_url that changes whenever the file's contents change.
    this.cacheKey = `pincode-data:${this.dataUrl ?? ''}`;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleOptionKeydown = this.handleOptionKeydown.bind(this);
    this.positionOverride = this.positionOverride.bind(this);

    this.toggleButton?.addEventListener('click', this.handleToggle);
    for (const option of this.options) {
      option.addEventListener('click', this.handleOptionClick);
      option.addEventListener('keydown', this.handleOptionKeydown);
    }
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.handleToggle);
    for (const option of this.options) {
      option.removeEventListener('click', this.handleOptionClick);
      option.removeEventListener('keydown', this.handleOptionKeydown);
    }
    this.stopWatchingOutsideInteraction();
  }

  handleToggle() {
    if (!this.overrideView) return;

    if (this.overrideView.hidden) {
      this.openOverride();
    } else {
      this.closeOverride();
    }
  }

  openOverride() {
    this.overrideView.hidden = false;
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    this.positionOverride();
    this.options[0]?.focus();
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.positionOverride);
  }

  closeOverride() {
    this.overrideView.hidden = true;
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    this.stopWatchingOutsideInteraction();
  }

  stopWatchingOutsideInteraction() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.positionOverride);
  }

  // Opens below the card by default. If there isn't enough viewport room below
  // for the panel but there's more room above, flips it to open upward instead
  // -- the same collision-avoidance a native <select> gets for free, which this
  // hand-built listbox has to do itself.
  positionOverride() {
    const anchor = this.querySelector('[data-pincode-default]');
    if (!anchor) return;

    const gap = 14;
    const anchorRect = anchor.getBoundingClientRect();
    const overrideHeight = this.overrideView.offsetHeight;
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const flipUp = spaceBelow < overrideHeight + gap && spaceAbove > spaceBelow;

    this.overrideView.classList.toggle('pincode-checker__override--flip-up', flipUp);
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) this.closeOverride();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.closeOverride();
  }

  handleOptionKeydown(event) {
    const { key } = event;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.selectCity(event.currentTarget);
      return;
    }

    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;

    event.preventDefault();
    const index = this.options.indexOf(event.currentTarget);
    const nextIndex = key === 'ArrowDown' ? index + 1 : index - 1;
    this.options[(nextIndex + this.options.length) % this.options.length]?.focus();
  }

  handleOptionClick(event) {
    this.selectCity(event.currentTarget);
  }

  async selectCity(optionElement) {
    const city = optionElement.dataset.value;

    for (const option of this.options) {
      option.setAttribute('aria-selected', option === optionElement ? 'true' : 'false');
    }

    try {
      const data = await this.getPincodeData();
      const entry = lookupEntry(data, city);
      // The option's visible label is the city name in the page's language
      // (Arabic on /ar - see snippets/pincode-delivery-check.liquid); the
      // JSON entry only carries the English name.
      this.renderResult(entry, optionElement.textContent.trim());
    } catch (error) {
      this.renderUnavailable();
    }
  }

  async getPincodeData() {
    if (!this.dataUrl) return null;

    const cached = sessionStorage.getItem(this.cacheKey);
    if (cached) return JSON.parse(cached);

    const response = await fetch(this.dataUrl);
    if (!response.ok) throw new Error(`Failed to load ${this.dataUrl}`);

    const data = await response.json();
    sessionStorage.setItem(this.cacheKey, JSON.stringify(data));

    return data;
  }

  renderResult(entry, cityLabel) {
    if (entry?.serviceable) {
      this.renderSuccess(entry, cityLabel);
    } else {
      this.renderUnavailable();
    }
  }

  renderSuccess(entry, cityLabel) {
    const deliveryDate = getExpectedDeliveryDate(entry.days_max);
    const fragment = this.successTemplate.content.cloneNode(true);
    // Only the date is filled in: the "Get it by" label is plain text in the
    // template (snippets/pincode-delivery-check.liquid) so LangShop can
    // translate it.
    const deliveryDateField = fragment.querySelector('[data-field="delivery-date"]');

    if (deliveryDateField) {
      deliveryDateField.textContent = deliveryDate;
    }

    this.result.replaceChildren(fragment);
    this.result.hidden = false;

    if (this.defaultCity) this.defaultCity.textContent = cityLabel || entry.city;
    if (this.defaultDate) this.defaultDate.textContent = deliveryDate;
  }

  renderUnavailable() {
    const fragment = this.unavailableTemplate.content.cloneNode(true);

    this.result.replaceChildren(fragment);
    this.result.hidden = false;
  }
}

if (!customElements.get('pincode-checker')) {
  customElements.define('pincode-checker', PincodeChecker);
}
